/**
 * The timezone an order's dates are read in.
 *
 * One order, one clock: every card in a thread — a visit, the availability the homeowner
 * shared, the picker that captured it — resolves its hours through here, so a homeowner
 * in Bangkok and a coordinator in New York read the SAME hours off the same slot. The
 * source is the requester's `TIME_ZONE` profile, which the user DTO already exposes
 * pre-parsed as `timezone` (the raw profile value is a JSON-quoted string, so reading the
 * profile array by hand yields `"America/New_York"` — quotes included — and every
 * date-fns-tz call built from it silently falls back).
 *
 * The viewer's own timezone is the fallback, not the default: it is what the surface
 * showed before the order row (or the requester's profile) is readable, and it is the
 * right answer for a viewer who IS the requester (the homeowner, in the common case).
 *
 * Two reads, both shared: the order detail is already in cache on any office screen that
 * opened the order, and the user detail is cached for the process (`staleTime: Infinity`).
 * On the member portal the order read is an extra request — `/orders/:orderId` loads every
 * relation whatever `?includes=` says — but it is one per order, not one per card.
 */

import { useGetOrdersByOrderId } from "@birdwatch/client";

import { useUserDetail } from "@/api/use-user-detail";
import useTimezone from "@/hooks/use-timezone";
import { DEFAULT_TIME_ZONE } from "@/libs/date-fns";

/** @param orderId - Pass `""` on a surface with no order; the viewer's timezone is used. */
export function useOrderRequesterTimeZone(orderId: string): string {
  const { timeZone: viewerTimeZone } = useTimezone();

  const { data: order } = useGetOrdersByOrderId({ orderId }).get({
    enabled: !!orderId,
  });
  const requesterId = order?.requesterId ?? "";

  const { data: requester } = useUserDetail(requesterId, {
    enabled: !!requesterId,
  });

  return requester?.timezone || viewerTimeZone || DEFAULT_TIME_ZONE;
}
